import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Warehouse,
  MapPin,
  TrendingUp,
  ArrowRightLeft,
  Settings,
  LogOut,
  Boxes,
  Cpu,
  ClipboardList,
  Image,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { usePreferencesStore } from '@/store/preferencesStore';

export const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', activeClass: 'bg-gray-100 dark:bg-charcoal-700 text-gray-900 dark:text-warm-cream border-gray-200 dark:border-charcoal-600' },
  { path: '/supplies', icon: Boxes, label: 'Supplies', activeClass: 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/20' },
  { path: '/products', icon: Package, label: 'Products', activeClass: 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-300 border-green-200 dark:border-green-500/20' },
  { path: '/production', icon: Cpu, label: 'Production', activeClass: 'bg-orange-100 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-500/20' },
  { path: '/inventory', icon: Warehouse, label: 'Inventory', activeClass: 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-500/20' },
  { path: '/sales', icon: TrendingUp, label: 'Sales', activeClass: 'bg-yellow-100 dark:bg-yellow-500/10 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-500/20' },
  { path: '/transfers', icon: ArrowRightLeft, label: 'Transfers', activeClass: 'bg-stone-100 dark:bg-stone-500/10 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-500/20' },
  { path: '/storage-locations', icon: MapPin, label: 'Locations', activeClass: 'bg-teal-100 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-500/20' },
  { path: '/audit-log', icon: ClipboardList, label: 'Audit Log', activeClass: 'bg-gray-100 dark:bg-charcoal-700 text-gray-900 dark:text-warm-cream border-gray-200 dark:border-charcoal-600' },
  { path: '/admin/media', icon: Image, label: 'Media Library', adminOnly: true, activeClass: 'bg-cyan-100 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-200 dark:border-cyan-500/20' },
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function Sidebar({ isOpen, onClose, collapsed = false, onToggleCollapse }: SidebarProps) {
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const widthClass = collapsed ? 'w-[4.5rem]' : 'w-64';
  const visibleNavItems = navItems.filter((item) => !('adminOnly' in item) || user?.role === 'ADMIN');

  const sidebarContent = (
    <aside
      className={`${widthClass} bg-white dark:bg-charcoal-800 border-r border-gray-200 dark:border-charcoal-700 flex flex-col h-full transition-all duration-200`}
    >
      <div className={`border-b border-gray-200 dark:border-charcoal-700 flex items-center ${collapsed ? 'p-3 justify-center' : 'p-6 justify-between'}`}>
        {!collapsed && (
          <div className="min-w-0">
            <h1 className="font-sans text-xl font-bold text-gray-900 dark:text-warm-cream truncate">
              Fun 1st Treasures
            </h1>
            <p className="text-xs text-gray-500 dark:text-warm-sand mt-1">Inventory System</p>
          </div>
        )}
        <div className="flex items-center gap-1">
          {onToggleCollapse && (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="hidden lg:flex p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-charcoal-700 min-h-[44px] min-w-[44px] items-center justify-center"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-charcoal-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <nav className="flex-1 p-2 lg:p-4 space-y-1 overflow-y-auto">
        {visibleNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={onClose}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg text-sm font-medium transition-all min-h-[44px] ${
                collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'
              } ${
                isActive
                  ? `border ${item.activeClass}`
                  : 'text-gray-600 dark:text-warm-sand hover:bg-gray-100 dark:hover:bg-charcoal-700 hover:text-gray-900 dark:hover:text-warm-cream'
              }`
            }
          >
            <item.icon className="w-5 h-5 shrink-0" />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className={`border-t border-gray-200 dark:border-charcoal-700 space-y-1 ${collapsed ? 'p-2' : 'p-4'}`}>
        <NavLink
          to="/settings"
          onClick={onClose}
          title={collapsed ? 'Settings' : undefined}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg text-sm font-medium transition-all min-h-[44px] ${
              collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'
            } ${
              isActive
                ? 'bg-amber-100 dark:bg-amber-450/10 text-amber-700 dark:text-amber-450 border border-amber-200 dark:border-amber-450/20'
                : 'text-gray-600 dark:text-warm-sand hover:bg-gray-100 dark:hover:bg-charcoal-700'
            }`
          }
        >
          <Settings className="w-5 h-5 shrink-0" />
          {!collapsed && 'Settings'}
        </NavLink>
        <button
          type="button"
          onClick={logout}
          title={collapsed ? 'Logout' : undefined}
          className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium text-gray-600 dark:text-warm-sand hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 transition-all min-h-[44px] ${
            collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'
          }`}
        >
          <LogOut className="w-5 h-5 shrink-0" />
          {!collapsed && 'Logout'}
        </button>
      </div>
    </aside>
  );

  return (
    <>
      <div className="hidden lg:flex shrink-0">{sidebarContent}</div>
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
          <div className="relative z-10 flex h-full">{sidebarContent}</div>
        </div>
      )}
    </>
  );
}

export function SidebarPreferencesBridge() {
  const { sidebarCollapsed, toggleSidebarCollapsed } = usePreferencesStore();
  return { sidebarCollapsed, toggleSidebarCollapsed };
}
